import type {
  TrackInitOptions,
  PvOptions,
  TraceData,
  TrackerAdapter,
  TrackPublicAPI,
} from './types';
import {
  PERF_EVENT_PREFIX,
  DEFAULT_ERROR_SAMPLE_RATE,
  DEFAULT_ERROR_MAX_COUNT,
  DEFAULT_ERROR_DEDUP_WINDOW,
} from './constants';
import { isBrowser, getPageUrl } from './utils';
import { loadSDK } from './loader';
import { DataRangersAdapter } from './adapters/datarangers';
import { setupPerfObservers } from './modules/perf';
import { setupErrorObservers, type ErrorReporter } from './modules/error';

/** Default init options */
const DEFAULT_OPTIONS: Partial<TrackInitOptions> = {
  enableDebug: false,
  disableSdkMonitor: true,
  reportFcp: true,
  reportLcp: true,
  reportError: true,
  reportResourceError: true,
  errorSampleRate: DEFAULT_ERROR_SAMPLE_RATE,
  errorMaxCount: DEFAULT_ERROR_MAX_COUNT,
  errorDedupWindow: DEFAULT_ERROR_DEDUP_WINDOW,
  errorIgnoreList: [],
};

/**
 * TrackSDK — main orchestrator class.
 * Wraps the underlying analytics SDK and exposes a clean API.
 */
class TrackSDK {
  private adapter: TrackerAdapter;
  private _ready = false;
  private _initPromise?: Promise<void>;
  private _pendingCalls: Array<() => void> = [];
  private _errorReporter?: ErrorReporter;

  constructor() {
    this.adapter = new DataRangersAdapter();
  }

  /**
   * Initialize the tracking SDK.
   * Mounts the public API immediately, then loads the SDK script,
   * calls init + start, sets up perf observers, and flushes queued calls.
   */
  init(appId: number, options?: Omit<TrackInitOptions, 'appId'>): Promise<void> {
    if (this._ready) {
      console.warn('[yl-nsm-track] Already initialized. Skipping re-init.');
      return Promise.resolve();
    }

    if (this._initPromise) {
      console.warn('[yl-nsm-track] Initialization already in progress. Reusing init promise.');
      return this._initPromise;
    }

    // Mount first so SPA route hooks can safely call window.$track immediately after init().
    this._mountToWindow();

    this._initPromise = this._initialize(appId, options).catch((error) => {
      this._initPromise = undefined;
      throw error;
    });

    return this._initPromise;
  }

  /** Track a custom event (single event, NOT batch) */
  event(eventName: string, properties?: Record<string, any>): void {
    const payload = properties ? { ...properties } : undefined;
    this._runWhenReady(() => this.adapter.event(eventName, payload));
  }

  /** Report a page view */
  pv(options?: PvOptions): void {
    // SPA 路由切换：重置错误限频计数与去重缓存，让新页面获得全新的错误预算
    this._errorReporter?.resetCount();

    const payload = options
      ? {
          ...options,
          extra: options.extra ? { ...options.extra } : options.extra,
        }
      : undefined;
    this._runWhenReady(() => this.adapter.pageView(payload));
  }

  /** Report a performance metric manually */
  trace(metricName: string, data: TraceData): void {
    const eventName = `${PERF_EVENT_PREFIX}${metricName.toLowerCase()}`;
    const payload = {
      metric_name: metricName,
      value: data.value,
      rating: data.rating,
      page_url: getPageUrl(),
      ...Object.fromEntries(
        Object.entries(data).filter(([k]) => !['value', 'rating'].includes(k)),
      ),
    };

    this._runWhenReady(() => this.adapter.event(eventName, payload));
  }

  /** 手动上报错误（不经过采样，但仍受去重 / 限频约束） */
  captureError(error: Error | string, extra?: Record<string, any>): void {
    this._runWhenReady(() => {
      if (this._errorReporter) {
        this._errorReporter.captureError(error, extra);
      }
    });
  }

  /** Login user (UV association) */
  login(userId: string): void {
    this._runWhenReady(() => this.adapter.login(userId));
  }

  /** Logout user */
  logout(): void {
    this._runWhenReady(() => this.adapter.logout());
  }

  private async _initialize(
    appId: number,
    options?: Omit<TrackInitOptions, 'appId'>,
  ): Promise<void> {
    const opts: TrackInitOptions = {
      ...DEFAULT_OPTIONS,
      ...options,
      appId,
    };

    // Step 1: Load the SDK script (creates command queue stub first)
    await loadSDK(opts.sdkScriptUrl);

    // Step 2: Init and start the SDK
    const initConfig: Record<string, any> = {};
    if (opts.channelDomain) initConfig.channel_domain = opts.channelDomain;
    if (opts.enableDebug) initConfig.enable_debug = true;
    if (opts.disableSdkMonitor) initConfig.disable_sdk_monitor = true;
    if (opts.globalProperties) initConfig.auto_track_properties = opts.globalProperties;

    this.adapter.init(appId, initConfig);
    this.adapter.start();

    // Step 3: Mark ready and replay calls made while the SDK was loading
    this._ready = true;
    this._flushPendingCalls();

    // Step 4: Setup FCP/LCP performance observers
    setupPerfObservers(this.adapter, {
      reportFcp: opts.reportFcp !== false,
      reportLcp: opts.reportLcp !== false,
      debug: opts.enableDebug === true,
    });

    // Step 5: 注册错误观察器（运行时 / Promise / 资源）
    this._errorReporter = setupErrorObservers(this.adapter, {
      reportError: opts.reportError !== false,
      reportResourceError: opts.reportResourceError !== false,
      sampleRate: opts.errorSampleRate ?? DEFAULT_ERROR_SAMPLE_RATE,
      maxCount: opts.errorMaxCount ?? DEFAULT_ERROR_MAX_COUNT,
      dedupWindow: opts.errorDedupWindow ?? DEFAULT_ERROR_DEDUP_WINDOW,
      ignoreList: opts.errorIgnoreList ?? [],
      debug: opts.enableDebug === true,
    });
  }

  private _runWhenReady(fn: () => void): void {
    if (this._ready) {
      fn();
      return;
    }

    if (!this._initPromise) {
      fn();
      return;
    }

    this._pendingCalls.push(fn);
  }

  private _flushPendingCalls(): void {
    const calls = this._pendingCalls.splice(0);

    for (const call of calls) {
      try {
        call();
      } catch (error) {
        console.warn('[yl-nsm-track] Failed to flush queued call', error);
      }
    }
  }

  /** Mount public API onto window.$track */
  private _mountToWindow(): void {
    if (!isBrowser()) return;

    const api: TrackPublicAPI = {
      $event: this.event.bind(this),
      $pv: this.pv.bind(this),
      $trace: this.trace.bind(this),
      $error: this.captureError.bind(this),
      $login: this.login.bind(this),
      $logout: this.logout.bind(this),
    };

    try {
      Object.defineProperty(window, '$track', {
        value: api,
        writable: false,
        configurable: true, // allows re-init or cleanup
      });
    } catch {
      // Fallback for environments where defineProperty fails
      (window as any).$track = api;
    }
  }
}

/** Singleton instance */
export const trackSDK = new TrackSDK();
